import React, { useCallback, useEffect, useState } from "react";
import { deletePasskey, listPasskeys, passkeysSupported, registerPasskey } from "../services/passkey";
import { Modal, Button } from "@mantine/core";
import {
  IconInfoCircle,
  IconFingerprint,
  IconShield,
  IconMail,
  IconPhone,
  IconLock,
} from "@tabler/icons-react";
import { FaUserLock } from "react-icons/fa";

const Security = () => {
    const [opened, setOpened] = useState(false);
  const [modalTitle, setModalTitle] = useState("");

  const [passkeys, setPasskeys] = useState([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyMessage, setPasskeyMessage] = useState({ type: "", text: "" });

  const loadPasskeys = useCallback(() => {
    listPasskeys().then(setPasskeys).catch(() => {});
  }, []);

  useEffect(() => {
    loadPasskeys();
  }, [loadPasskeys]);

  const addPasskey = async () => {
    setPasskeyBusy(true);
    setPasskeyMessage({ type: "", text: "" });
    try {
      await registerPasskey(`Passkey ${passkeys.length + 1}`);
      setPasskeyMessage({ type: "success", text: "Passkey added. You can now sign in with it." });
      loadPasskeys();
    } catch (error) {
      setPasskeyMessage({ type: "error", text: error.message });
    } finally {
      setPasskeyBusy(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm("Remove this passkey?")) return;
    try {
      await deletePasskey(id);
      loadPasskeys();
    } catch (error) {
      setPasskeyMessage({ type: "error", text: error.message });
    }
  };

  const openModal = (title) => {
    setModalTitle(title);
    setOpened(true);
  }
  return (
    <div>
      <div className="max-w-6xl p-6 rounded-lg border">
      {/* Header */}
      <h1 className="text-2xl font-bold mb-4">Two-Factor Authentication (2FA)</h1>

      {/* Info Banner */}
      <div className="flex items-start gap-2 bg-yellow-50 text-yellow-800 p-4 rounded mb-6">
        <IconInfoCircle size={20} className="mt-0.5" />
        <div>
          <p>
            To increase your account security, it is recommended that you enable
            2FA, including Google Authenticator or another authenticator app.
          </p>
          <button className="text-yellow-600 font-medium hover:underline">
            Enable an Authenticator App Now
          </button>
        </div>
      </div>

      {/* Auth Options */}
      <div className="space-y-6">
        {/* Passkeys */}
        <div className="lg:flex block items-center justify-between">
          <div className="flex gap-3">
            <IconFingerprint size={28} />
            <div>
              <p className="font-medium">Passkeys (Biometrics)</p>
              <p className="text-gray-500 text-sm">
                Protect your account and withdrawals with Passkeys and/or
                security keys, such as Yubikey.
              </p>
              {passkeyMessage.text && (
                <p className={`text-sm mt-1 ${passkeyMessage.type === "error" ? "text-red-600" : "text-green-600"}`}>{passkeyMessage.text}</p>
              )}
              {passkeys.length > 0 && (
                <ul className="mt-2 space-y-1 text-sm">
                  {passkeys.map((item) => (
                    <li key={item.id} className="flex items-center gap-3">
                      <span>{item.name}</span>
                      <span className="text-gray-500">
                        {item.last_used_at ? `Last used ${new Date(item.last_used_at).toLocaleDateString()}` : "Never used"}
                      </span>
                      <button type="button" className="text-red-600 hover:underline" onClick={() => removePasskey(item.id)}>Remove</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between lg:gap-3">
            <span className="text-gray-500 flex items-center gap-1">
              {passkeys.length > 0 ? `✔ ON (${passkeys.length})` : "✖ OFF"}
            </span>
            <Button variant="default" onClick={addPasskey} loading={passkeyBusy} disabled={!passkeysSupported()}>
              {passkeys.length > 0 ? "Add another" : "Enable"}
            </Button>
          </div>
        </div>

        {/* Authenticator App */}
        <div className="lg:flex block items-center justify-between">
          <div className="flex gap-3">
            <IconShield size={28} />
            <div>
              <p className="font-medium">Authenticator App</p>
              <p className="text-gray-500 text-sm">
                Use an authenticator app to protect your account and
                transactions.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between lg:gap-3">
            <span className="text-gray-500">✖ OFF</span>
            <Button variant="default" onClick={() => openModal("Manage Authenticator App")}>
              Manage
            </Button>
          </div>
        </div>

        {/* Email */}
        <div className="lg:flex block items-center justify-between">
          <div className="flex gap-3">
            <IconMail size={28} />
            <div>
              <p className="font-medium">Email</p>
              <p className="text-gray-500 text-sm">
                Use your email to protect your account and transactions.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between lg:gap-3">
            <span className="text-gray-500">OFF</span>
            <Button variant="default" onClick={() => openModal("Manage Email")}>
              Manage
            </Button>
          </div>
        </div>

        {/* Phone Number */}
        <div className="lg:flex block items-center justify-between">
          <div className="flex gap-3">
            <IconPhone size={28} />
            <div>
              <p className="font-medium">Phone Number</p>
              <p className="text-gray-500 text-sm">
                Use your phone number to protect your account and transactions.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between lg:gap-3">
            <span className="text-green-600">✔ 987****346</span>
            <Button variant="default" onClick={() => openModal("Manage Phone Number")}>
              Manage
            </Button>
          </div>
        </div>

        {/* Login Password */}
        <div className="lg:flex block items-center justify-between">
          <div className="flex gap-3">
            <IconLock size={28} />
            <div>
              <p className="font-medium">Login Password</p>
              <p className="text-gray-500 text-sm">
                Login password is used to log in to your account.
              </p>
            </div>
          </div>
          <Button variant="default" onClick={() => openModal("Manage Login Password")} className="lg:mt-0 mt-4 justify-end">
            Manage
          </Button>
        </div>
      </div>

      {/* Modal */}
      <Modal opened={opened} onClose={() => setOpened(false)} title={modalTitle}>
        <p>This is where your {modalTitle.toLowerCase()} flow would go.</p>
      </Modal>
    </div>
    <div className="max-w-6xl mt-10 bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-xl font-bold mb-6">Account Management</h2>

      {/* Disable Account */}
      <div className="flex items-start justify-between border-b pb-6 mb-6">
        <div className="flex items-start gap-3">
          <FaUserLock className="text-gray-500 text-lg mt-1" />
          <div>
            <h3 className="font-semibold">Disable Account</h3>
            <p className="text-gray-500 text-sm max-w-2xl">
              Once the account is disabled, most of your actions will be
              restricted, such as logging in and trading. You can choose to
              unblock the account at any time. This action will not delete your
              account.
            </p>
          </div>
        </div>
        <button className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-1 rounded">
          Disable
        </button>
      </div>

      {/* Delete Account */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <FaUserLock className="text-gray-500 text-lg mt-1" />
          <div>
            <h3 className="font-semibold">Delete Account</h3>
            <p className="text-gray-500 text-sm max-w-2xl">
              Please note that account deletion is irreversible. Once deleted,
              you will not be able to access your account or view your
              transaction history.
            </p>
          </div>
        </div>
        <button className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-1 rounded">
          Delete
        </button>
      </div>
    </div>

    </div>
  )
}

export default Security